<template>
  <wizard-question :title="$t('Configura le caratteristiche del server')">
    <pf-grid gutter>
      <pf-grid-item span="6" lg="3"><cpu-icon /> {{ $t('CPU') }}:</pf-grid-item>
      <pf-grid-item span="6" :lg="wizard.modal ? 5 : 3">
        <x-input
          type="number"
          name="cpuQtn"
          required
          :min="minimi.cpu"
          :help="helpMinimo(minimi.cpu, 'Core')"
          :custom-validity="validityMinimo(cpuQtn, minimi.cpu, 'Core')"
          :max="MAX_CPU"
          append="Core"
          :model-value="cpuQtn"
          @update:model-value="emit('update:cpuQtn', Number($event))"
        />
      </pf-grid-item>
      <pf-grid-item span="12" :lg="wizard.modal ? 4 : 6" class="pf-v6-u-text-align-end pf-v6-u-text-align-start-on-lg">
        <wizard-price-tag :product="wizard.getProdotto(wizard.codiciCloud.cpu)" :qt="cpuQtn" />
      </pf-grid-item>

      <pf-grid-item span="6" lg="3"><memory-icon /> {{ $t('RAM') }}:</pf-grid-item>
      <pf-grid-item span="6" :lg="wizard.modal ? 5 : 3">
        <x-input
          type="number"
          name="ramQtn"
          required
          :min="minimi.ram"
          :help="helpMinimo(minimi.ram, 'GB')"
          :custom-validity="validityMinimo(ramQtn, minimi.ram, 'GB')"
          :max="MAX_RAM"
          append="GB"
          :model-value="ramQtn"
          @update:model-value="emit('update:ramQtn', Number($event))"
        />
      </pf-grid-item>
      <pf-grid-item span="12" :lg="wizard.modal ? 4 : 6" class="pf-v6-u-text-align-end pf-v6-u-text-align-start-on-lg">
        <wizard-price-tag :product="wizard.getProdotto(wizard.codiciCloud.ram)" :qt="ramQtn" />
      </pf-grid-item>

      <pf-grid-item span="6" lg="3"><volume-icon /> {{ $t('Storage') }}:</pf-grid-item>
      <pf-grid-item span="6" :lg="wizard.modal ? 5 : 3">
        <x-input
          type="number"
          name="storageQtn"
          required
          :min="minimi.storage"
          :help="helpMinimo(minimi.storage, 'GB')"
          :custom-validity="validityMinimo(storageQtn, minimi.storage, 'GB')"
          :max="MAX_STORAGE"
          :step="10"
          append="GB"
          :model-value="storageQtn"
          @update:model-value="emit('update:storageQtn', Number($event))"
        />
      </pf-grid-item>
      <pf-grid-item span="12" :lg="wizard.modal ? 4 : 6" class="pf-v6-u-text-align-end pf-v6-u-text-align-start-on-lg">
        <wizard-price-tag :product="wizard.getProdotto(wizard.codiciCloud.storage)" :qt="storageQtn" />
      </pf-grid-item>

      <template v-if="wizard.os === 'windows'">
        <pf-grid-item span="6" lg="3"> <id-card-icon /> {{ $t('Licenza sistema operativo') }}: </pf-grid-item>
        <pf-grid-item span="6" :lg="wizard.modal ? 5 : 3">
          <x-input type="number" disabled append="Core" :aria-label="$t('Licenza sistema operativo')" :model-value="Math.round(cpuQtn)" />
        </pf-grid-item>
        <pf-grid-item span="12" :lg="wizard.modal ? 4 : 6" class="pf-v6-u-text-align-end pf-v6-u-text-align-start-on-lg">
          <wizard-price-tag :product="wizard.getProdotto(wizard.codiciProdottiAddon.winServer)" :qt="Math.round(cpuQtn / 2)" />
        </pf-grid-item>
      </template>
    </pf-grid>

    <wizard-price-tag :product="wizard.serverCloud" :label="$t('Prezzo totale server')" />

    <pf-button v-if="limiteRaggiunto" type="button" variant="primary" small class="pf-v6-u-mt-sm" @click="wizard.path = 'host'">
      {{ $t('Vuoi di più? Passa a un host server dedicato') }}
    </pf-button>
  </wizard-question>
</template>

<style lang="scss" scoped>
:deep(.pf-v6-c-input-group__item.pf-m-box) {
  min-width: 6ch;
}
</style>

<script setup lang="ts">
import { computed } from 'vue';
import { $t as _t } from '@/i18n';
import WizardPriceTag from '../wizard-price-tag.vue';
import { useWizard } from '@/wizard';
import CpuIcon from '@vue-patternfly/icons/cpu-icon';
import MemoryIcon from '@vue-patternfly/icons/memory-icon';
import VolumeIcon from '@vue-patternfly/icons/volume-icon';
import IdCardIcon from '@vue-patternfly/icons/id-card-icon';
import WizardQuestion from '../questions/wizard-question.vue';

const props = defineProps<{
  cpuQtn: number;
  ramQtn: number;
  storageQtn: number;
}>();

const emit = defineEmits<{
  (name: `update:${'cpu' | 'ram' | 'storage'}Qtn`, v: number): void;
}>();

const MAX_CPU = 32;
const MAX_RAM = 256;
const MAX_STORAGE = 1000;

const wizard = useWizard();

// Il wizard modale della pagina server resta sul percorso cloud
const limiteRaggiunto = computed(
  () => !wizard.modal && (props.cpuQtn >= MAX_CPU || props.ramQtn >= MAX_RAM || props.storageQtn >= MAX_STORAGE),
);

// Se è già stato scelto uno stack (tornando indietro nel wizard) i minimi sono quelli richiesti dallo stack
const minimi = computed(() => {
  const stack = wizard.stack ? wizard.stacks[wizard.stack] : undefined;
  return {
    cpu: stack?.minCpu ?? 1,
    ram: stack?.minRam ?? 1,
    storage: stack?.minStorage ?? 20,
  };
});

function helpMinimo(minimo: number, um: string) {
  return wizard.stack ? _t('Minimo richiesto dallo stack {0}: {1} {2}', [wizard.stack, minimo, um]) : undefined;
}

function validityMinimo(valore: number, minimo: number, um: string) {
  return wizard.stack && valore < minimo ? _t('Lo stack {0} richiede almeno {1} {2}', [wizard.stack, minimo, um]) : '';
}
</script>
